import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PermissionService } from '../services/permission.service';
import { UtilsService } from '../services/utils.service';

/**
 * Bloquea la navegación por URL a módulos que el rol del usuario no tiene
 * activos, incluso si el usuario está autenticado (AuthGuard solo valida
 * sesión, no permisos). Se aplica en el MISMO nivel de ruta que AuthGuard
 * (la ruta padre de cada routing module), así que `route.routeConfig.path`
 * es exactamente el path registrado para ese módulo (p. ej. "unit",
 * "unit/departure-checks", "maintenance/expense-history").
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {

    constructor(
        private permissionService: PermissionService,
        private utils: UtilsService,
        private router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const dataUser = this.utils.getDataUser();
        const userId = dataUser?.id;
        const routePath = route.routeConfig?.path ?? '';

        if (!userId) {
            this.router.navigate(['/login']);
            return of(false);
        }

        return this.permissionService.loadForUser(userId).pipe(
            map(() => {
                if (this.hasAccessToPath(this.permissionService.getModules(), routePath)) {
                    return true;
                }
                const fallback = this.permissionService.getFirstAccessibleRoute();
                this.router.navigate(fallback ?? ['/login']);
                return false;
            }),
            catchError(() => {
                this.router.navigate(['/login']);
                return of(false);
            })
        );
    }

    private hasAccessToPath(nodes: any[], routePath: string): boolean {
        const pathSegments = String(routePath).split('/').filter(Boolean);
        if (!pathSegments.length) return true;
        return this.matchesAny(nodes, pathSegments);
    }

    private matchesAny(nodes: any[], pathSegments: string[]): boolean {
        for (const n of nodes || []) {
            const routeSegments = String(n?.route || '').split(/[./]/).filter(Boolean);
            if (routeSegments.length && this.isPrefixMatch(routeSegments, pathSegments)) return true;
            if (Array.isArray(n?.children) && n.children.length && this.matchesAny(n.children, pathSegments)) return true;
        }
        return false;
    }

    /** true si el segmento más corto de los dos es exactamente el inicio del más largo. */
    private isPrefixMatch(a: string[], b: string[]): boolean {
        const len = Math.min(a.length, b.length);
        if (len === 0) return false;
        for (let i = 0; i < len; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }
}
